import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ═══════════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════════ */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

:root {
  --primary: #0A0F1E;
  --accent:  #00D68F;
  --white:   #ffffff;
  --text:    #0A0F1E;
  --text-muted: #6B7280;
  --border:  #E5E7EB;
  --surface: #F9FAFB;
  --surface2: #F3F4F6;
  --radius:  14px;
  --radius-sm: 8px;
  --sidebar-w: 260px;
  --content-max: 760px;
  --code-bg: #0D1117;
}

/* ── Reset ── */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.docs {
  font-family: 'DM Sans', sans-serif;
  color: var(--text);
  background: var(--white);
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
}

/* ── Top bar ── */
.docs-topbar {
  position: fixed; top: 0; left: 0; right: 0; z-index: 200;
  height: 60px;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 24px;
  gap: 16px;
}
.docs-logo {
  display: flex; align-items: center; gap: 10px;
  text-decoration: none; font-weight: 700; font-size: 1.1rem; color: var(--primary);
  flex-shrink: 0;
}
.docs-logo-icon {
  width: 32px; height: 32px; background: var(--primary); border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  color: var(--accent); font-weight: 700; font-size: 1rem;
}
.docs-logo-sep {
  width: 1px; height: 20px; background: var(--border); margin: 0 4px;
}
.docs-logo-tag {
  font-size: 0.78rem; font-weight: 600; color: var(--text-muted);
  background: var(--surface); padding: 3px 8px; border-radius: 6px;
  border: 1px solid var(--border);
}
.docs-topbar-search {
  flex: 1; max-width: 380px;
  display: flex; align-items: center; gap: 10px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 0 14px; height: 36px;
  cursor: text; transition: border-color 0.2s;
}
.docs-topbar-search:focus-within { border-color: var(--accent); }
.docs-topbar-search input {
  border: none; background: transparent; outline: none;
  font-family: inherit; font-size: 0.88rem; color: var(--text);
  flex: 1; width: 100%;
}
.docs-topbar-right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.docs-topbar-right a {
  font-size: 0.85rem; font-weight: 500; color: var(--text-muted);
  text-decoration: none; transition: color 0.2s;
}
.docs-topbar-right a:hover { color: var(--primary); }
.docs-topbar-right .btn-api {
  background: var(--accent); color: var(--primary); font-weight: 700;
  padding: 7px 16px; border-radius: var(--radius-sm); font-size: 0.82rem;
}

/* ── Layout ── */
.docs-layout {
  display: grid;
  grid-template-columns: var(--sidebar-w) 1fr 220px;
  min-height: 100vh;
  padding-top: 60px;
}

/* ── Left sidebar ── */
.docs-sidebar {
  position: sticky; top: 60px; height: calc(100vh - 60px);
  overflow-y: auto; border-right: 1px solid var(--border);
  padding: 28px 0 40px;
  scrollbar-width: thin;
}
.docs-sidebar::-webkit-scrollbar { width: 4px; }
.docs-sidebar::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }

.sidebar-section-title {
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.12em; color: var(--text-muted);
  padding: 20px 20px 8px; display: block;
}
.sidebar-section-title:first-child { padding-top: 0; }

.sidebar-link {
  display: flex; align-items: center; gap: 8px;
  padding: 7px 20px; font-size: 0.875rem; font-weight: 500;
  color: var(--text-muted); text-decoration: none; border-radius: 0;
  border-left: 2px solid transparent; transition: all 0.15s; cursor: pointer;
  background: none; border-top: none; border-right: none; border-bottom: none;
  width: 100%; text-align: left; font-family: inherit;
}
.sidebar-link:hover { color: var(--primary); background: var(--surface); }
.sidebar-link.active { color: var(--accent); font-weight: 600; border-left-color: var(--accent); background: rgba(0,214,143,0.05); }
.sidebar-link .method-badge {
  font-family: 'JetBrains Mono', monospace; font-size: 0.6rem;
  font-weight: 700; padding: 1px 5px; border-radius: 3px; flex-shrink: 0;
}
.badge-get    { background: rgba(5,150,105,0.12); color: #059669; }
.badge-post   { background: rgba(59,130,246,0.12); color: #3B82F6; }
.badge-put    { background: rgba(217,119,6,0.12); color: #D97706; }
.badge-delete { background: rgba(220,38,38,0.12); color: #DC2626; }

/* ── Main content ── */
.docs-content {
  padding: 48px 56px;
  max-width: calc(var(--content-max) + 112px);
  min-width: 0;
}

/* Sections */
.doc-section { margin-bottom: 80px; scroll-margin-top: 80px; }
.doc-section h1 {
  font-family: 'Instrument Serif', serif; font-weight: 400;
  font-size: 2.4rem; line-height: 1.15; color: var(--primary);
  margin-bottom: 16px; letter-spacing: -0.02em;
}
.doc-section h2 {
  font-size: 1.4rem; font-weight: 700; color: var(--primary);
  margin: 48px 0 16px; padding-bottom: 12px;
  border-bottom: 1px solid var(--border);
}
.doc-section h3 {
  font-size: 1.05rem; font-weight: 700; color: var(--primary);
  margin: 32px 0 12px;
}
.doc-section p {
  font-size: 0.95rem; line-height: 1.75; color: #374151;
  margin-bottom: 16px;
}
.doc-section ul, .doc-section ol {
  padding-left: 20px; margin-bottom: 16px;
}
.doc-section li {
  font-size: 0.95rem; line-height: 1.75; color: #374151; margin-bottom: 4px;
}
.doc-section a { color: var(--accent); text-decoration: none; font-weight: 500; }
.doc-section a:hover { text-decoration: underline; }

/* Callouts */
.callout {
  display: flex; gap: 12px; padding: 16px 18px;
  border-radius: var(--radius-sm); margin: 20px 0; font-size: 0.88rem; line-height: 1.6;
}
.callout-info { background: rgba(59,130,246,0.06); border: 1px solid rgba(59,130,246,0.2); color: #1e40af; }
.callout-warn { background: rgba(245,158,11,0.06); border: 1px solid rgba(245,158,11,0.25); color: #92400e; }
.callout-tip  { background: rgba(0,214,143,0.06); border: 1px solid rgba(0,214,143,0.25); color: #065f46; }

/* Inline code */
.docs-content code:not(.block) {
  font-family: 'JetBrains Mono', monospace; font-size: 0.82em;
  background: var(--surface2); color: #9333ea;
  padding: 2px 6px; border-radius: 4px; border: 1px solid var(--border);
}

/* Code blocks */
.code-block {
  background: var(--code-bg); border-radius: var(--radius-sm);
  overflow: hidden; margin: 20px 0;
  border: 1px solid rgba(255,255,255,0.06);
  box-shadow: 0 4px 24px rgba(0,0,0,0.12);
}
.code-block-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; border-bottom: 1px solid rgba(255,255,255,0.06);
  background: rgba(255,255,255,0.03);
}
.code-block-lang {
  font-family: 'JetBrains Mono', monospace; font-size: 0.72rem;
  color: rgba(255,255,255,0.35); text-transform: uppercase; letter-spacing: 0.08em;
}
.code-block-tabs { display: flex; gap: 2px; }
.code-tab {
  font-family: 'JetBrains Mono', monospace; font-size: 0.72rem;
  padding: 4px 10px; border-radius: 4px; cursor: pointer; border: none;
  background: transparent; color: rgba(255,255,255,0.3); transition: all 0.15s; font-family: inherit;
}
.code-tab.active { background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.85); }
.code-copy-btn {
  font-size: 0.72rem; color: rgba(255,255,255,0.3); background: none;
  border: none; cursor: pointer; font-family: inherit; padding: 4px 8px;
  border-radius: 4px; transition: all 0.15s;
}
.code-copy-btn:hover { color: rgba(255,255,255,0.7); background: rgba(255,255,255,0.08); }
.code-block pre {
  margin: 0; padding: 20px; overflow-x: auto;
  font-family: 'JetBrains Mono', monospace; font-size: 0.8rem;
  line-height: 1.8; color: rgba(255,255,255,0.82);
}
/* Syntax colors */
.t-comment { color: rgba(255,255,255,0.25); }
.t-keyword  { color: #7DD3FC; }
.t-string   { color: #86EFAC; }
.t-number   { color: #FCA5A5; }
.t-prop     { color: #C4B5FD; }
.t-method   { color: #FDE68A; }
.t-type     { color: #6EE7B7; }
.t-url      { color: #93C5FD; }
.t-accent   { color: #00D68F; }

/* Endpoint block */
.endpoint-block {
  border: 1px solid var(--border); border-radius: var(--radius);
  overflow: hidden; margin: 24px 0;
}
.endpoint-header {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 20px; background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.endpoint-method {
  font-family: 'JetBrains Mono', monospace; font-size: 0.75rem;
  font-weight: 700; padding: 4px 10px; border-radius: 5px; flex-shrink: 0;
}
.endpoint-path {
  font-family: 'JetBrains Mono', monospace; font-size: 0.9rem;
  font-weight: 500; color: var(--primary); flex: 1;
}
.endpoint-desc { font-size: 0.82rem; color: var(--text-muted); }
.endpoint-body { padding: 20px; }

/* Params table */
.params-table {
  width: 100%; border-collapse: collapse; font-size: 0.85rem; margin: 12px 0;
}
.params-table th {
  text-align: left; font-size: 0.7rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-muted);
  padding: 8px 12px; border-bottom: 2px solid var(--border);
}
.params-table td {
  padding: 10px 12px; border-bottom: 1px solid var(--border);
  vertical-align: top; line-height: 1.5;
}
.params-table tr:last-child td { border-bottom: none; }
.params-table td:first-child {
  font-family: 'JetBrains Mono', monospace; font-size: 0.8rem;
  color: #9333ea; font-weight: 500; white-space: nowrap;
}
.params-table td:nth-child(2) {
  font-family: 'JetBrains Mono', monospace; font-size: 0.75rem;
  color: var(--text-muted);
}
.required-badge {
  font-size: 0.65rem; font-weight: 700; color: #DC2626;
  background: rgba(220,38,38,0.08); padding: 1px 5px;
  border-radius: 3px; margin-left: 6px; vertical-align: middle;
}
.optional-badge {
  font-size: 0.65rem; font-weight: 700; color: var(--text-muted);
  background: var(--surface2); padding: 1px 5px;
  border-radius: 3px; margin-left: 6px; vertical-align: middle;
}

/* Response block */
.response-tabs { display: flex; gap: 0; border-bottom: 1px solid var(--border); margin-bottom: 16px; }
.response-tab {
  padding: 8px 16px; font-size: 0.82rem; font-weight: 600;
  border: none; background: none; cursor: pointer; font-family: inherit;
  color: var(--text-muted); border-bottom: 2px solid transparent;
  transition: all 0.15s; margin-bottom: -1px;
}
.response-tab.active { color: var(--primary); border-bottom-color: var(--primary); }
.response-tab .status { font-family: 'JetBrains Mono', monospace; font-size: 0.75rem; }
.status-200 { color: #059669; }
.status-400 { color: #D97706; }
.status-401 { color: #DC2626; }
.status-429 { color: #7C3AED; }

/* Right-side TOC */
.docs-toc {
  position: sticky; top: 60px; height: calc(100vh - 60px);
  overflow-y: auto; padding: 28px 20px 40px;
  border-left: 1px solid var(--border); font-size: 0.8rem;
}
.toc-title {
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.12em; color: var(--text-muted); margin-bottom: 12px;
}
.toc-link {
  display: block; padding: 4px 0 4px 10px;
  border-left: 1.5px solid transparent; color: var(--text-muted);
  text-decoration: none; transition: all 0.15s; line-height: 1.4;
  font-size: 0.8rem;
}
.toc-link:hover { color: var(--primary); border-left-color: var(--border); }
.toc-link.active { color: var(--accent); border-left-color: var(--accent); font-weight: 600; }
.toc-link.sub { padding-left: 20px; font-size: 0.75rem; }

/* Divider */
.doc-divider { border: none; border-top: 1px solid var(--border); margin: 48px 0; }

/* Mobile */
@media(max-width: 1100px) { .docs-toc { display: none; } .docs-layout { grid-template-columns: var(--sidebar-w) 1fr; } }
@media(max-width: 768px) {
  .docs-layout { grid-template-columns: 1fr; }
  .docs-sidebar { display: none; }
  .docs-content { padding: 32px 20px; }
  .docs-topbar-search { display: none; }
}
`;

/* ═══════════════════════════════════════════════════════════════
   DATA
═══════════════════════════════════════════════════════════════ */
const NAV = [
  { group: 'Introdução', items: [
    { id: 'visao-geral',   label: 'Visão geral' },
    { id: 'autenticacao',  label: 'Autenticação' },
    { id: 'erros',         label: 'Erros e códigos' },
    { id: 'rate-limit',    label: 'Rate limiting' },
    { id: 'versionamento', label: 'Versionamento' },
  ]},
  { group: 'Quick Start', items: [
    { id: 'instalacao',    label: 'Instalação SDK' },
    { id: 'primeiro-call', label: 'Primeiro request' },
  ]},
  { group: 'Endpoints', items: [
    { id: 'ep-simulate',   label: 'Simular',       method: 'POST' },
    { id: 'ep-banks',      label: 'Listar bancos',  method: 'GET'  },
    { id: 'ep-batch',      label: 'Batch',          method: 'POST' },
    { id: 'ep-result',     label: 'Resultado',      method: 'GET'  },
    { id: 'ep-webhooks',   label: 'Webhooks',       method: 'POST' },
    { id: 'ep-usage',      label: 'Uso da conta',   method: 'GET'  },
  ]},
  { group: 'SDKs', items: [
    { id: 'sdk-csharp',    label: 'C# / .NET' },
    { id: 'sdk-node',      label: 'Node.js' },
    { id: 'sdk-python',    label: 'Python' },
    { id: 'sdk-curl',      label: 'cURL' },
  ]},
  { group: 'Referência', items: [
    { id: 'ref-bancos',    label: 'Bancos suportados' },
    { id: 'ref-modelos',   label: 'Modelos de dados' },
    { id: 'ref-glossario', label: 'Glossário' },
    { id: 'changelog',     label: 'Changelog' },
  ]},
];

const METHOD_STYLE = {
  GET:    { bg:'rgba(5,150,105,0.1)',  color:'#059669' },
  POST:   { bg:'rgba(59,130,246,0.1)', color:'#3B82F6' },
  PUT:    { bg:'rgba(217,119,6,0.1)',  color:'#D97706' },
  DELETE: { bg:'rgba(220,38,38,0.1)', color:'#DC2626'  },
};

/* ── Code snippets ── */
const SNIPPETS = {
  install: {
    csharp: `# .NET CLI
dotnet add package SimulaBank.Client

# Package Manager
Install-Package SimulaBank.Client`,
    node:   `npm install @simulabank/sdk
# ou
yarn add @simulabank/sdk`,
    python: `pip install simulabank`,
    curl:   `# Não requer instalação — use curl nativo`,
  },

  init: {
    csharp: `using SimulaBank.Client;

// Inicialize o cliente com sua chave de API
var client = new SimulaBankClient("sb_live_SuaChaveAqui");

// Ou via appsettings.json (recomendado para produção)
var client = new SimulaBankClient(
    builder.Configuration["SimulaBank:ApiKey"]
);`,
    node: `import SimulaBank from '@simulabank/sdk';

const client = new SimulaBank({
  apiKey: process.env.SIMULABANK_API_KEY,
});`,
    python: `import simulabank

client = simulabank.Client(api_key="sb_live_SuaChaveAqui")`,
    curl: `# Defina sua chave como variável de ambiente
export SIMULABANK_API_KEY="sb_live_SuaChaveAqui"`,
  },

  simulate: {
    csharp: `var result = await client.SimulateAsync(new SimulationRequest
{
    PropertyValue  = 500_000m,   // Valor do imóvel
    FinancedAmount = 400_000m,   // Valor financiado
    TermMonths     = 360,        // Prazo em meses (30 anos)
    BorrowerAge    = 35,         // Idade do mutuário
    Banks          = Bank.All,   // Todos os bancos disponíveis
    // Banks = new[] { Bank.Caixa, Bank.BRB } // ou selecione
});

Console.WriteLine($"Melhor banco: {result.BestOption.BankName}");
Console.WriteLine($"1ª parcela:   {result.BestOption.FirstInstallment:C}");
Console.WriteLine($"CET anual:    {result.BestOption.CET:P2}");`,

    node: `const result = await client.simulate({
  propertyValue:  500000,
  financedAmount: 400000,
  termMonths:     360,
  borrowerAge:    35,
  banks:          'all',  // ou ['caixa', 'brb', 'inter']
});

console.log('Melhor banco:', result.bestOption.bankName);
console.log('1ª parcela:',   result.bestOption.firstInstallment);`,

    python: `result = client.simulate(
    property_value=500_000,
    financed_amount=400_000,
    term_months=360,
    borrower_age=35,
    banks="all",
)

print(f"Melhor banco: {result.best_option.bank_name}")
print(f"1ª parcela:   R$ {result.best_option.first_installment:.2f}")`,

    curl: `curl -X POST https://api.simulabank.com.br/v1/simulate \\
  -H "Authorization: Bearer $SIMULABANK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "propertyValue":  500000,
    "financedAmount": 400000,
    "termMonths":     360,
    "borrowerAge":    35,
    "banks":          "all"
  }'`,
  },

  response_simulate: `{
  "id": "sim_01HX9K2B4MFQR6Y8Z3D7N5P",
  "status": "completed",
  "createdAt": "2026-02-28T14:32:10Z",
  "bestOption": {
    "bankCode": "inter",
    "bankName": "Banco Inter",
    "firstInstallment": 3842.17,
    "lastInstallment":  2104.38,
    "totalCost":        1_382_401.20,
    "totalInterest":    982_401.20,
    "cet":              0.1089,
    "rate":             0.0085
  },
  "results": [
    {
      "bankCode":          "inter",
      "bankName":          "Banco Inter",
      "rate":              0.0085,
      "firstInstallment":  3842.17,
      "lastInstallment":   2104.38,
      "totalCost":         1382401.20,
      "cet":               0.1089,
      "mip":               120.00,
      "dfi":               42.50,
      "installments":      [{ "month": 1, "payment": 3842.17, "principal": 1111.11, "interest": 3400.00, "mip": 120.00, "dfi": 42.50, "balance": 398888.89 }, "..."]
    },
    { "bankCode": "caixa",    "..." : "..." },
    { "bankCode": "brb",      "..." : "..." },
    { "bankCode": "bradesco", "..." : "..." },
    { "bankCode": "santander","..." : "..." },
    { "bankCode": "itau",     "..." : "..." }
  ]
}`,

  error_response: `// 400 — Parâmetros inválidos
{
  "error": {
    "code":    "validation_error",
    "message": "O valor financiado não pode ser maior que o valor do imóvel.",
    "field":   "financedAmount",
    "docs":    "https://docs.simulabank.com.br/erros#validation_error"
  }
}

// 401 — Chave inválida
{
  "error": {
    "code":    "unauthorized",
    "message": "API key inválida ou expirada.",
    "docs":    "https://docs.simulabank.com.br/autenticacao"
  }
}

// 429 — Rate limit
{
  "error": {
    "code":       "rate_limit_exceeded",
    "message":    "Limite de requisições atingido.",
    "retryAfter": 12,
    "limit":      1000,
    "remaining":  0,
    "reset":      "2026-02-28T14:33:00Z"
  }
}`,

  webhook_payload: `{
  "event": "simulation.completed",
  "id":    "evt_01HX9K2B4MFQR6Y8Z3D7N5P",
  "createdAt": "2026-02-28T14:32:10Z",
  "data": {
    "simulationId": "sim_01HX9K2B4MFQR6Y8Z3D7N5P",
    "bestOption": { "bankCode": "inter", "firstInstallment": 3842.17 }
  }
}`,
};

/* ═══════════════════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════════════════ */

function CodeBlock({ tabs, defaultTab, snippets, lang }) {
  const [active, setActive] = useState(defaultTab || (tabs ? tabs[0] : null));
  const [copied, setCopied] = useState(false);

  const code = tabs ? snippets[active] : snippets;

  const copy = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple syntax highlighter
  const highlight = (line) => {
    if (!line.trim()) return <span> </span>;
    if (line.trim().startsWith('//') || line.trim().startsWith('#'))
      return <span className="t-comment">{line}</span>;

    return line.split(/("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|\b(?:var|const|let|new|await|using|return|async|public|private|string|int|decimal|bool|from|import|export|default|class)\b|\b\d+(?:[_\d]*\d)?\b|\b(?:SimulaBankClient|SimulationRequest|Bank|client|result)\b|\$\{[^}]+\})/g).map((part, i) => {
      if (/^"/.test(part) || /^'/.test(part)) return <span key={i} className="t-string">{part}</span>;
      if (/^\b(var|const|let|new|await|using|return|async|public|private|string|int|decimal|bool|from|import|export|default|class)\b$/.test(part)) return <span key={i} className="t-keyword">{part}</span>;
      if (/^\d/.test(part)) return <span key={i} className="t-number">{part}</span>;
      if (/^(SimulaBankClient|SimulationRequest|Bank|client|result)$/.test(part)) return <span key={i} className="t-type">{part}</span>;
      if (/^\$\{/.test(part)) return <span key={i} className="t-prop">{part}</span>;
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="code-block">
      <div className="code-block-header">
        {tabs ? (
          <div className="code-block-tabs">
            {tabs.map(t => (
              <button key={t} className={`code-tab${active === t ? ' active' : ''}`} onClick={() => setActive(t)}>{t}</button>
            ))}
          </div>
        ) : (
          <span className="code-block-lang">{lang || 'json'}</span>
        )}
        <button className="code-copy-btn" onClick={copy}>{copied ? '✓ copiado' : 'copiar'}</button>
      </div>
      <pre>
        {code.split('\n').map((line, i) => (
          <div key={i}>{highlight(line)}</div>
        ))}
      </pre>
    </div>
  );
}

function EndpointBlock({ method, path, desc, children }) {
  const s = METHOD_STYLE[method] || METHOD_STYLE.GET;
  return (
    <div className="endpoint-block">
      <div className="endpoint-header">
        <span className="endpoint-method" style={{ background: s.bg, color: s.color }}>{method}</span>
        <code className="endpoint-path">{path}</code>
        {desc && <span className="endpoint-desc">{desc}</span>}
      </div>
      <div className="endpoint-body">{children}</div>
    </div>
  );
}

function ParamsTable({ rows }) {
  return (
    <table className="params-table">
      <thead>
        <tr>
          <th>Campo</th><th>Tipo</th><th>Descrição</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([name, type, desc, req]) => (
          <tr key={name}>
            <td>{name}{req ? <span className="required-badge">required</span> : <span className="optional-badge">optional</span>}</td>
            <td>{type}</td>
            <td>{desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Callout({ type = 'info', icon, children }) {
  const icons = { info: 'ℹ️', warn: '⚠️', tip: '💡' };
  return (
    <div className={`callout callout-${type}`}>
      <span style={{ flexShrink: 0 }}>{icon || icons[type]}</span>
      <div>{children}</div>
    </div>
  );
}

function ResponseTabs({ tabs }) {
  const [active, setActive] = useState(0);
  const statusColor = { 200: 'status-200', 201: 'status-200', 400: 'status-400', 401: 'status-401', 429: 'status-429' };
  return (
    <div>
      <div className="response-tabs">
        {tabs.map((t, i) => (
          <button key={i} className={`response-tab${active === i ? ' active' : ''}`} onClick={() => setActive(i)}>
            <span className={`status ${statusColor[t.status] || ''}`}>{t.status}</span>{' '}{t.label}
          </button>
        ))}
      </div>
      <CodeBlock snippets={tabs[active].code} lang="json" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════ */
export default function DocsPage() {
  const [activeSection, setActiveSection] = useState('visao-geral');
  const [search, setSearch] = useState('');
  const contentRef = useRef(null);
  const SDK_TABS = ['C#', 'Node.js', 'Python', 'cURL'];
  const SDK_KEYS = { 'C#': 'csharp', 'Node.js': 'node', 'Python': 'python', 'cURL': 'curl' };

  // Track active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-60px 0px -70% 0px', threshold: 0 }
    );
    document.querySelectorAll('.doc-section[id]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  const BASE_URL = 'https://api.simulabank.com.br';

  return (
    <div className="docs">
      <style>{css}</style>

      {/* ══ TOP BAR ══ */}
      <header className="docs-topbar">
        <Link to="/" className="docs-logo">
          <div className="docs-logo-icon">S</div>
          SimulaBank
          <div className="docs-logo-sep" />
          <span className="docs-logo-tag">Docs</span>
        </Link>

        <div className="docs-topbar-search" onClick={() => document.getElementById('docs-search').focus()}>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>🔍</span>
          <input id="docs-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar na documentação... (⌘K)" />
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', flexShrink: 0 }}>⌘K</span>
        </div>

        <div className="docs-topbar-right">
          <Link to="/api">API</Link>
          <Link to="/status">Status</Link>
          <Link to="/ajuda">Suporte</Link>
          <Link to="/cadastro" className="btn-api">Começar grátis →</Link>
        </div>
      </header>

      {/* ══ LAYOUT ══ */}
      <div className="docs-layout">

        {/* ── LEFT SIDEBAR ── */}
        <nav className="docs-sidebar">
          {NAV.map(group => (
            <div key={group.group}>
              <span className="sidebar-section-title">{group.group}</span>
              {group.items.map(item => (
                <button
                  key={item.id}
                  className={`sidebar-link${activeSection === item.id ? ' active' : ''}`}
                  onClick={() => scrollTo(item.id)}
                >
                  {item.method && (
                    <span className={`method-badge badge-${item.method.toLowerCase()}`}>{item.method}</span>
                  )}
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* ── MAIN CONTENT ── */}
        <main className="docs-content" ref={contentRef}>

          {/* ════ VISÃO GERAL ════ */}
          <section id="visao-geral" className="doc-section">
            <h1>Documentação da API</h1>
            <p>A <strong>SimulaBank API</strong> permite integrar simulações de financiamento imobiliário diretamente no seu sistema, app ou plataforma. Com um único request, você obtém cálculos precisos de SAC, MIP, DFI e CET para os 6 maiores bancos do Brasil.</p>

            <Callout type="warn">
              <strong>A API está em beta fechado.</strong> Cadastre-se na <Link to="/api">lista de espera</Link> para receber acesso antecipado. Os endpoints e modelos podem mudar antes do lançamento público.
            </Callout>

            <h3>URL base</h3>
            <CodeBlock snippets={`${BASE_URL}/v1`} lang="text" />

            <h3>Formatos suportados</h3>
            <p>Todas as requisições e respostas utilizam <code>JSON</code>. Certifique-se de incluir o header <code>Content-Type: application/json</code> em requisições com body.</p>

            <h3>Bancos suportados</h3>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, margin:'16px 0' }}>
              {[['BRB','#003882'],['Santander','#EC0000'],['Itaú','#FF6600'],['Inter','#FF500F'],['Bradesco','#CC092F'],['Caixa','#005CA9']].map(([name, cor]) => (
                <div key={name} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--surface)' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:cor, flexShrink:0 }} />
                  <span style={{ fontSize:'0.88rem', fontWeight:500 }}>{name}</span>
                </div>
              ))}
            </div>
          </section>

          <hr className="doc-divider" />

          {/* ════ AUTENTICAÇÃO ════ */}
          <section id="autenticacao" className="doc-section">
            <h2>Autenticação</h2>
            <p>A API utiliza <strong>Bearer Token</strong> via header <code>Authorization</code>. Todas as requisições devem incluir sua chave de API.</p>

            <CodeBlock lang="text" snippets={`Authorization: Bearer sb_live_SuaChaveAqui`} />

            <h3>Tipos de chave</h3>
            <ParamsTable rows={[
              ['sb_live_...', 'string', 'Chave de produção. Afeta dados reais. Use em ambientes de produção.', false],
              ['sb_test_...', 'string', 'Chave de teste. Sem cobrança de créditos. Retorna dados mockados realistas.', false],
            ]} />

            <Callout type="tip">
              Nunca exponha sua chave no frontend ou em repositórios públicos. Use variáveis de ambiente e <code>appsettings.json</code> em projetos C#.
            </Callout>

            <h3>Gerenciar chaves</h3>
            <p>Acesse o painel em <Link to="/dashboard">simulabank.com.br/dashboard</Link> → Configurações → API Keys para criar, rotacionar ou revogar chaves.</p>
          </section>

          <hr className="doc-divider" />

          {/* ════ ERROS ════ */}
          <section id="erros" className="doc-section">
            <h2>Erros e códigos HTTP</h2>
            <p>A API usa códigos HTTP padrão. Todos os erros retornam um objeto <code>error</code> com informações detalhadas.</p>

            <ParamsTable rows={[
              ['200', 'OK',                    'Requisição bem-sucedida.',                                  false],
              ['201', 'Created',               'Recurso criado com sucesso.',                               false],
              ['400', 'Bad Request',           'Parâmetros inválidos. Verifique o campo error.field.',      false],
              ['401', 'Unauthorized',          'Chave de API ausente ou inválida.',                         false],
              ['404', 'Not Found',             'Recurso não encontrado.',                                   false],
              ['422', 'Unprocessable Entity',  'Dados semanticamente inválidos (ex: LTV acima de 90%).',    false],
              ['429', 'Too Many Requests',     'Rate limit atingido. Verifique o header Retry-After.',      false],
              ['500', 'Internal Server Error', 'Erro interno. Verifique simulabank.com.br/status.',         false],
            ]} />

            <h3>Estrutura do erro</h3>
            <CodeBlock lang="json" snippets={SNIPPETS.error_response} />
          </section>

          <hr className="doc-divider" />

          {/* ════ RATE LIMIT ════ */}
          <section id="rate-limit" className="doc-section">
            <h2>Rate Limiting</h2>
            <p>Os limites variam por plano. Os headers de resposta informam o status atual:</p>

            <ParamsTable rows={[
              ['X-RateLimit-Limit',     'integer', 'Total de requisições permitidas na janela atual.',  false],
              ['X-RateLimit-Remaining', 'integer', 'Requisições restantes na janela.',                  false],
              ['X-RateLimit-Reset',     'ISO 8601', 'Timestamp em que a janela reinicia.',              false],
              ['Retry-After',           'integer', 'Segundos para aguardar ao receber 429.',             false],
            ]} />

            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, margin:'16px 0' }}>
              {[['Starter','10.000 req/mês','~14 req/min'],['Growth','50.000 req/mês','~69 req/min'],['Scale','Ilimitado','1.000+ req/min']].map(([p,m,r]) => (
                <div key={p} style={{ padding:'16px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--surface)' }}>
                  <div style={{ fontWeight:700, fontSize:'0.88rem', marginBottom:4 }}>{p}</div>
                  <div style={{ fontSize:'0.82rem', color:'var(--accent)', fontWeight:600 }}>{m}</div>
                  <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2 }}>{r}</div>
                </div>
              ))}
            </div>
          </section>

          <hr className="doc-divider" />

          {/* ════ VERSIONAMENTO ════ */}
          <section id="versionamento" className="doc-section">
            <h2>Versionamento</h2>
            <p>A versão da API é especificada na URL (<code>/v1/</code>). Novas versões são anunciadas com 90 dias de antecedência. Versões antigas são mantidas por pelo menos 12 meses após deprecação.</p>
            <Callout type="info">A versão atual é <strong>v1</strong> (beta). Durante o beta, campos podem ser adicionados sem aviso prévio — seu código deve ser tolerante a campos desconhecidos.</Callout>
          </section>

          <hr className="doc-divider" />

          {/* ════ INSTALAÇÃO ════ */}
          <section id="instalacao" className="doc-section">
            <h2>Instalação do SDK</h2>
            <p>Escolha o SDK para sua linguagem. Todos os SDKs são wrappers tipados sobre a API REST.</p>
            <CodeBlock
              tabs={SDK_TABS}
              defaultTab="C#"
              snippets={Object.fromEntries(SDK_TABS.map(t => [t, SNIPPETS.install[SDK_KEYS[t]]]))}
            />

            <h3>Inicialização</h3>
            <CodeBlock
              tabs={SDK_TABS}
              defaultTab="C#"
              snippets={Object.fromEntries(SDK_TABS.map(t => [t, SNIPPETS.init[SDK_KEYS[t]]]))}
            />
          </section>

          <hr className="doc-divider" />

          {/* ════ PRIMEIRO REQUEST ════ */}
          <section id="primeiro-call" className="doc-section">
            <h2>Primeiro request</h2>
            <p>Com o SDK inicializado, faça sua primeira simulação em menos de 10 linhas:</p>
            <CodeBlock
              tabs={SDK_TABS}
              defaultTab="C#"
              snippets={Object.fromEntries(SDK_TABS.map(t => [t, SNIPPETS.simulate[SDK_KEYS[t]]]))}
            />
            <h3>Resposta</h3>
            <CodeBlock lang="json" snippets={SNIPPETS.response_simulate} />
          </section>

          <hr className="doc-divider" />

          {/* ════ EP: SIMULATE ════ */}
          <section id="ep-simulate" className="doc-section">
            <h2>POST /v1/simulate</h2>
            <p>Executa uma simulação de financiamento imobiliário nos bancos selecionados e retorna todos os resultados ordenados pelo menor custo total.</p>

            <EndpointBlock method="POST" path="/v1/simulate" desc="Simula financiamento em múltiplos bancos">
              <h3>Body parameters</h3>
              <ParamsTable rows={[
                ['propertyValue',  'decimal', 'Valor total do imóvel em R$. Mínimo: R$ 100.000.', true],
                ['financedAmount', 'decimal', 'Valor a ser financiado. Deve ser menor que propertyValue. LTV máximo de 90%.', true],
                ['termMonths',     'integer', 'Prazo do financiamento em meses. Mínimo: 12. Máximo: 420 (35 anos).', true],
                ['borrowerAge',    'integer', 'Idade do mutuário em anos. Usado para calcular MIP. Mínimo: 18.', true],
                ['banks',          'string | string[]', 'Bancos a simular. Use "all" ou array de codes: "brb", "santander", "itau", "inter", "bradesco", "caixa".', false],
                ['referenceDate',  'date',    'Data base da simulação (ISO 8601). Default: hoje.', false],
                ['customRates',    'object',  'Taxas personalizadas por banco. Substitui as taxas padrão. Ex: {"brb": 0.0082}', false],
              ]} />

              <h3>Resposta</h3>
              <ResponseTabs tabs={[
                { status: 200, label: 'Sucesso', code: SNIPPETS.response_simulate },
                { status: 400, label: 'Inválido', code: `{\n  "error": {\n    "code": "validation_error",\n    "message": "O valor financiado excede 90% do valor do imóvel (LTV: 95%).",\n    "field": "financedAmount"\n  }\n}` },
                { status: 401, label: 'Não autorizado', code: `{\n  "error": {\n    "code": "unauthorized",\n    "message": "API key inválida ou ausente."\n  }\n}` },
              ]} />
            </EndpointBlock>
          </section>

          <hr className="doc-divider" />

          {/* ════ EP: BANKS ════ */}
          <section id="ep-banks" className="doc-section">
            <h2>GET /v1/banks</h2>
            <p>Retorna a lista de todos os bancos suportados com suas taxas de juros atuais e metadados.</p>

            <EndpointBlock method="GET" path="/v1/banks" desc="Lista bancos e taxas">
              <h3>Query parameters</h3>
              <ParamsTable rows={[
                ['active', 'boolean', 'Se true, retorna apenas bancos com taxa disponível. Default: true.', false],
              ]} />
              <h3>Resposta</h3>
              <CodeBlock lang="json" snippets={`{\n  "banks": [\n    {\n      "code":        "inter",\n      "name":        "Banco Inter",\n      "color":       "#FF500F",\n      "currentRate": 0.0085,\n      "rateUpdatedAt": "2026-02-01T00:00:00Z",\n      "active":      true,\n      "minLoan":     100000,\n      "maxLoan":     5000000,\n      "maxTerm":     360,\n      "maxAge":      80\n    },\n    { "code": "caixa", "..." : "..." },\n    { "code": "brb",   "..." : "..." }\n  ],\n  "updatedAt": "2026-02-01T00:00:00Z"\n}`} />
            </EndpointBlock>
          </section>

          <hr className="doc-divider" />

          {/* ════ EP: BATCH ════ */}
          <section id="ep-batch" className="doc-section">
            <h2>POST /v1/simulate/batch</h2>
            <p>Processa múltiplas simulações em uma única requisição. Ideal para importação em massa ou relatórios de carteira.</p>

            <Callout type="info">Disponível apenas nos planos Growth e Scale. Máximo de <strong>100 simulações</strong> por batch. Processamento assíncrono — você recebe um <code>batchId</code> e consulta o resultado via webhook ou polling.</Callout>

            <EndpointBlock method="POST" path="/v1/simulate/batch" desc="Processa múltiplas simulações">
              <h3>Body</h3>
              <CodeBlock lang="json" snippets={`{\n  "simulations": [\n    { "propertyValue": 500000, "financedAmount": 400000, "termMonths": 360, "borrowerAge": 35, "banks": "all" },\n    { "propertyValue": 300000, "financedAmount": 240000, "termMonths": 240, "borrowerAge": 42, "banks": ["caixa", "itau"] }\n  ],\n  "webhookUrl": "https://sua-app.com/webhooks/simulabank"\n}`} />
              <h3>Resposta (202 Accepted)</h3>
              <CodeBlock lang="json" snippets={`{\n  "batchId":    "bat_01HX9K2B4MFQR6Y8Z3D7N5P",\n  "status":     "processing",\n  "total":      2,\n  "estimatedMs": 800\n}`} />
            </EndpointBlock>
          </section>

          <hr className="doc-divider" />

          {/* ════ EP: RESULT ════ */}
          <section id="ep-result" className="doc-section">
            <h2>GET /v1/simulate/{'{id}'}</h2>
            <p>Recupera o resultado de uma simulação específica pelo seu ID.</p>

            <EndpointBlock method="GET" path="/v1/simulate/{id}" desc="Busca simulação por ID">
              <h3>Path parameters</h3>
              <ParamsTable rows={[
                ['id', 'string', 'ID da simulação retornado pelo endpoint POST /v1/simulate.', true],
              ]} />
              <Callout type="info">Simulações ficam disponíveis por <strong>30 dias</strong>. Após isso retornam 404.</Callout>
            </EndpointBlock>
          </section>

          <hr className="doc-divider" />

          {/* ════ EP: WEBHOOKS ════ */}
          <section id="ep-webhooks" className="doc-section">
            <h2>Webhooks</h2>
            <p>Webhooks notificam sua aplicação quando eventos ocorrem na API, como a conclusão de um batch ou atualização de taxas.</p>

            <h3>Eventos disponíveis</h3>
            <ParamsTable rows={[
              ['simulation.completed', 'string', 'Simulação concluída (útil para batch assíncrono).', false],
              ['batch.completed',      'string', 'Todos os itens de um batch foram processados.',     false],
              ['rates.updated',        'string', 'Taxas de juros de um ou mais bancos foram atualizadas.', false],
            ]} />

            <h3>Payload</h3>
            <CodeBlock lang="json" snippets={SNIPPETS.webhook_payload} />

            <h3>Validação da assinatura</h3>
            <p>Cada request de webhook inclui o header <code>X-SimulaBank-Signature</code> com um HMAC-SHA256 do body usando seu <strong>webhook secret</strong>.</p>
            <CodeBlock
              tabs={['C#', 'Node.js']}
              defaultTab="C#"
              snippets={{
                'C#': `// Valide no seu endpoint de webhook
[HttpPost("/webhooks/simulabank")]
public IActionResult Webhook([FromBody] string payload, [FromHeader(Name = "X-SimulaBank-Signature")] string sig)
{
    var secret = _config["SimulaBank:WebhookSecret"];
    var expected = SimulaBankClient.ComputeSignature(payload, secret);
    if (!string.Equals(sig, expected, StringComparison.OrdinalIgnoreCase))
        return Unauthorized();

    // Processe o evento...
    return Ok();
}`,
                'Node.js': `import crypto from 'crypto';

app.post('/webhooks/simulabank', express.raw({ type: 'application/json' }), (req, res) => {
  const sig      = req.headers['x-simulabank-signature'];
  const expected = crypto.createHmac('sha256', process.env.WEBHOOK_SECRET)
                         .update(req.body).digest('hex');

  if (sig !== expected) return res.status(401).send('Signature inválida');

  const event = JSON.parse(req.body);
  // Processe o evento...
  res.sendStatus(200);
});`,
              }}
            />
          </section>

          <hr className="doc-divider" />

          {/* ════ EP: USAGE ════ */}
          <section id="ep-usage" className="doc-section">
            <h2>GET /v1/usage</h2>
            <p>Retorna o consumo de requisições da conta no período atual.</p>

            <EndpointBlock method="GET" path="/v1/usage" desc="Consulta uso e limites da conta">
              <h3>Resposta</h3>
              <CodeBlock lang="json" snippets={`{\n  "plan":       "growth",\n  "period": {\n    "start": "2026-02-01T00:00:00Z",\n    "end":   "2026-02-28T23:59:59Z"\n  },\n  "requests": {\n    "used":      12_840,\n    "limit":     50_000,\n    "remaining": 37_160,\n    "resetAt":   "2026-03-01T00:00:00Z"\n  },\n  "simulations": {\n    "total":  12_840,\n    "byBank": { "inter": 3200, "caixa": 2900, "brb": 2100 }\n  }\n}`} />
            </EndpointBlock>
          </section>

          <hr className="doc-divider" />

          {/* ════ SDKs ════ */}
          <section id="sdk-csharp" className="doc-section">
            <h2>SDK — C# / .NET</h2>
            <p>Compatível com .NET 6, 7, 8 e 9. Totalmente assíncrono com suporte a <code>CancellationToken</code>.</p>
            <CodeBlock lang="csharp" snippets={`using SimulaBank.Client;
using SimulaBank.Client.Models;

// Configuração via DI (recomendado para ASP.NET Core)
builder.Services.AddSimulaBank(options => {
    options.ApiKey  = builder.Configuration["SimulaBank:ApiKey"];
    options.Timeout = TimeSpan.FromSeconds(30);
});

// Uso no controller ou service
public class SimulacaoService(ISimulaBankClient client)
{
    public async Task<SimulationResult> SimularAsync(SimulacaoParams p, CancellationToken ct)
    {
        return await client.SimulateAsync(new SimulationRequest
        {
            PropertyValue  = p.ValorImovel,
            FinancedAmount = p.ValorFinanciado,
            TermMonths     = p.PrazoMeses,
            BorrowerAge    = p.IdadeMutuario,
            Banks          = Bank.All,
        }, ct);
    }
}`} />
          </section>

          <section id="sdk-node" className="doc-section">
            <h2>SDK — Node.js</h2>
            <p>Suporte a TypeScript nativo. Promise-based com tipagem completa.</p>
            <CodeBlock lang="typescript" snippets={`import SimulaBank, { SimulationRequest, SimulationResult } from '@simulabank/sdk';

const client = new SimulaBank({ apiKey: process.env.SIMULABANK_API_KEY! });

async function simular(params: SimulationRequest): Promise<SimulationResult> {
  return client.simulate({
    propertyValue:  params.propertyValue,
    financedAmount: params.financedAmount,
    termMonths:     params.termMonths,
    borrowerAge:    params.borrowerAge,
    banks:          'all',
  });
}`} />
          </section>

          <section id="sdk-python" className="doc-section">
            <h2>SDK — Python</h2>
            <p>Suporte a Python 3.8+. Disponível em versões síncrona e assíncrona (<code>asyncio</code>).</p>
            <CodeBlock lang="python" snippets={`import simulabank
import asyncio

# Síncrono
client = simulabank.Client(api_key="sb_live_...")
result = client.simulate(property_value=500_000, financed_amount=400_000, term_months=360, borrower_age=35)

# Assíncrono
async def main():
    async with simulabank.AsyncClient(api_key="sb_live_...") as client:
        result = await client.simulate(property_value=500_000, financed_amount=400_000, term_months=360, borrower_age=35)
        print(result.best_option.bank_name)

asyncio.run(main())`} />
          </section>

          <section id="sdk-curl" className="doc-section">
            <h2>cURL — Exemplos</h2>
            <p>Exemplos prontos para testar diretamente no terminal.</p>
            <CodeBlock lang="bash" snippets={`# ── Simular ──────────────────────────────────────
curl -X POST ${BASE_URL}/v1/simulate \\
  -H "Authorization: Bearer $SIMULABANK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"propertyValue":500000,"financedAmount":400000,"termMonths":360,"borrowerAge":35,"banks":"all"}'

# ── Listar bancos ──────────────────────────────────
curl ${BASE_URL}/v1/banks \\
  -H "Authorization: Bearer $SIMULABANK_API_KEY"

# ── Consultar uso ──────────────────────────────────
curl ${BASE_URL}/v1/usage \\
  -H "Authorization: Bearer $SIMULABANK_API_KEY"`} />
          </section>

          <hr className="doc-divider" />

          {/* ════ REFERÊNCIA — BANCOS ════ */}
          <section id="ref-bancos" className="doc-section">
            <h2>Bancos suportados</h2>
            <ParamsTable rows={[
              ['brb',       'BRB — Banco de Brasília',       'Taxa referência: 0,82% a.m. | Prazo máx: 360 meses', false],
              ['santander', 'Banco Santander',               'Taxa referência: 0,89% a.m. | Prazo máx: 360 meses', false],
              ['itau',      'Itaú Unibanco',                 'Taxa referência: 0,95% a.m. | Prazo máx: 360 meses', false],
              ['inter',     'Banco Inter',                   'Taxa referência: 0,85% a.m. | Prazo máx: 360 meses', false],
              ['bradesco',  'Banco Bradesco',                'Taxa referência: 0,92% a.m. | Prazo máx: 360 meses', false],
              ['caixa',     'Caixa Econômica Federal',       'Taxa referência: 0,87% a.m. | Prazo máx: 420 meses', false],
            ]} />
            <Callout type="info">Taxas são atualizadas mensalmente e podem variar. Consulte <code>GET /v1/banks</code> para as taxas em tempo real.</Callout>
          </section>

          <hr className="doc-divider" />

          {/* ════ REFERÊNCIA — MODELOS ════ */}
          <section id="ref-modelos" className="doc-section">
            <h2>Modelos de dados</h2>
            <h3>SimulationResult</h3>
            <ParamsTable rows={[
              ['id',           'string',        'ID único da simulação. Prefixo: sim_',                         false],
              ['status',       'string',        '"completed" | "processing" | "failed"',                        false],
              ['createdAt',    'ISO 8601',      'Timestamp de criação.',                                        false],
              ['bestOption',   'BankResult',    'Resultado do banco com menor custo total.',                    false],
              ['results',      'BankResult[]',  'Array de resultados, um por banco, ordenados por custo total.',false],
            ]} />
            <h3>BankResult</h3>
            <ParamsTable rows={[
              ['bankCode',          'string',  'Código do banco. Ex: "inter"',                         false],
              ['bankName',          'string',  'Nome legível do banco.',                                false],
              ['rate',              'decimal', 'Taxa de juros mensal aplicada (ex: 0.0085 = 0,85%).',  false],
              ['firstInstallment',  'decimal', 'Valor da primeira parcela em R$.',                     false],
              ['lastInstallment',   'decimal', 'Valor da última parcela em R$.',                       false],
              ['totalCost',         'decimal', 'Custo total do financiamento (principal + encargos).',  false],
              ['totalInterest',     'decimal', 'Total pago em juros.',                                 false],
              ['cet',               'decimal', 'CET anual (ex: 0.1089 = 10,89% a.a.).',               false],
              ['mip',               'decimal', 'Prêmio MIP da primeira parcela em R$.',                false],
              ['dfi',               'decimal', 'Prêmio DFI da primeira parcela em R$.',                false],
              ['installments',      'Installment[]', 'Tabela completa de parcelas (SAC mês a mês).',  false],
            ]} />
          </section>

          <hr className="doc-divider" />

          {/* ════ GLOSSÁRIO ════ */}
          <section id="ref-glossario" className="doc-section">
            <h2>Glossário</h2>
            {[
              ['SAC', 'Sistema de Amortização Constante. A amortização do principal é fixa, enquanto os juros diminuem ao longo do tempo — resultando em parcelas decrescentes. É o sistema predominante no crédito imobiliário brasileiro.'],
              ['MIP', 'Morte e Invalidez Permanente. Seguro obrigatório que quita o saldo devedor em caso de morte ou invalidez do mutuário. O prêmio é calculado sobre o saldo devedor e varia com a idade.'],
              ['DFI', 'Danos Físicos ao Imóvel. Seguro obrigatório que cobre danos estruturais ao imóvel financiado. Prêmio fixo calculado sobre o valor do imóvel.'],
              ['CET', 'Custo Efetivo Total. Taxa anual que inclui juros, seguros, tarifas e todos os encargos do financiamento. Exigido pelo Banco Central para transparência na comparação entre produtos.'],
              ['LTV', 'Loan-to-Value. Relação entre o valor financiado e o valor do imóvel (%). Ex: financiar R$ 400k em um imóvel de R$ 500k = LTV de 80%. A maioria dos bancos aceita até 80–90%.'],
              ['TR',  'Taxa Referencial. Índice criado em 1991, historicamente próximo de zero. Alguns financiamentos (especialmente Caixa/SBPE) usam TR como indexador além da taxa de juros.'],
            ].map(([t, d]) => (
              <div key={t} style={{ display:'flex', gap:16, padding:'16px 0', borderBottom:'1px solid var(--border)' }}>
                <code style={{ fontFamily:'JetBrains Mono, monospace', fontSize:'0.8rem', color:'#9333ea', fontWeight:600, minWidth:40, paddingTop:2 }}>{t}</code>
                <p style={{ margin:0, fontSize:'0.9rem', lineHeight:1.65 }}>{d}</p>
              </div>
            ))}
          </section>

          <hr className="doc-divider" />

          {/* ════ CHANGELOG ════ */}
          <section id="changelog" className="doc-section">
            <h2>Changelog</h2>
            {[
              { version:'v1.0.0-beta.3', date:'28 fev 2026', items:['Adicionado suporte a customRates no endpoint /simulate.','Novo campo totalInterest na resposta BankResult.','Melhoria de performance: P95 de latência reduziu de 320ms para 180ms.'] },
              { version:'v1.0.0-beta.2', date:'15 fev 2026', items:['Suporte a webhooks para eventos simulation.completed e rates.updated.','Endpoint GET /v1/usage agora inclui breakdown por banco.','Fix: cálculo de MIP para mutuários com 70+ anos estava retornando taxa incorreta.'] },
              { version:'v1.0.0-beta.1', date:'1 fev 2026', items:['Lançamento do beta fechado.','Endpoints: POST /simulate, GET /banks, GET /usage.','SDKs: C#, Node.js, Python.','Suporte a 6 bancos: BRB, Santander, Itaú, Inter, Bradesco e Caixa.'] },
            ].map(v => (
              <div key={v.version} style={{ marginBottom:36 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                  <code style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'0.85rem', color:'var(--accent)', fontWeight:700 }}>{v.version}</code>
                  <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{v.date}</span>
                </div>
                <ul>
                  {v.items.map((item,i) => <li key={i} style={{ fontSize:'0.9rem', lineHeight:1.65, color:'#374151', marginBottom:4 }}>{item}</li>)}
                </ul>
              </div>
            ))}
          </section>

        </main>

        {/* ── RIGHT TOC ── */}
        <aside className="docs-toc">
          <div className="toc-title">Nesta página</div>
          {NAV.flatMap(g => g.items).map(item => (
            <button
              key={item.id}
              className={`toc-link${activeSection === item.id ? ' active' : ''}`}
              onClick={() => scrollTo(item.id)}
              style={{ background:'none', border:'none', cursor:'pointer', width:'100%', textAlign:'left', fontFamily:'inherit' }}
            >
              {item.label}
            </button>
          ))}
        </aside>

      </div>
    </div>
  );
}
